import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { Logo } from './Logo';
import { FREE_LIMITS } from '../entitlements';
import { PRO_PRICE } from '../billing';

/**
 * Marketing landing shown at the root on every fresh visit — it's both the
 * crawlable, keyword-rich page that gives the SPA something to rank on
 * (rendered by React; Google executes JS) and the future signed-out home for
 * accounts/Pro. Returning users with an autosaved draft get a one-click
 * "Continue where you left off" that restores it straight away.
 */
export function Landing({
  onLaunch,
  onOpenExample,
  hasDraft,
}: {
  onLaunch: (resume?: boolean) => void;
  onOpenExample: () => void;
  hasDraft?: boolean;
}) {
  return (
    <div style={page}>
      <style>{MOTION_CSS}</style>
      <header style={navStyle}>
        <Logo size={30} />
        <div style={{ flex: 1 }} />
        <button style={navGhost} onClick={onOpenExample}>
          Example project
        </button>
      </header>

      <main style={heroWrap}>
        <section style={hero}>
          <div style={heroText}>
            <span style={eyebrow}>Browser-based electrical CAD · no install</span>
            <h1 style={h1}>Your machine&apos;s whole electrical design, in one organized place</h1>
            <p style={sub}>
              <strong>Skematiq</strong> keeps the entire electrical side of a machine together
              and consistent — subsystems, block diagrams, pin-to-pin schematics,{' '}
              <em>cable harnesses</em> and panel layout, all connected. Change a wire once and
              everything downstream follows. No more scattering it across CAD files,
              spreadsheets and PDFs. <span style={{ color: 'var(--text-dim)' }}>(Excel &amp; PDF
              exports are there too — just what falls out at the end.)</span>
            </p>
            <div style={ctaRow}>
              {hasDraft ? (
                <>
                  <button style={launchBig} onClick={() => onLaunch(true)}>
                    Continue where you left off →
                  </button>
                  <button style={launchGhost} onClick={() => onLaunch()}>
                    Open Skematiq
                  </button>
                </>
              ) : (
                <>
                  <button style={launchBig} onClick={() => onLaunch()}>
                    Open Skematiq — free to start
                  </button>
                  <button style={launchGhost} onClick={onOpenExample}>
                    See the example →
                  </button>
                </>
              )}
            </div>
            <span style={ctaNote}>Free plan · Pro from {PRO_PRICE}/mo · runs in your browser.</span>
            <ul style={chips} aria-label="What you can build">
              {HERO_CHIPS.map((c) => (
                <li key={c} style={chip}>
                  {c}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <Showcase />

        <section aria-label="Features" style={grid}>
          {FEATURES.map((f) => (
            <div key={f.title} style={card}>
              <h2 style={cardTitle}>{f.title}</h2>
              <p style={cardText}>{f.text}</p>
            </div>
          ))}
        </section>

        <section style={howWrap}>
          <h2 style={h2}>How it works</h2>
          <ol style={steps}>
            <li>Create a <strong>subsystem</strong> per enclosure (Control Box, Sensor Pod…) — each gets Requirements, Block diagram, Schematic and Mechanical sheets. Add a second and Skematiq builds a <strong>System Overview</strong> of the whole machine automatically.</li>
            <li>Drop components from the library and <strong>wire pin to pin</strong>; the label you type becomes the net.</li>
            <li>Select the wires that run together and turn them into a <strong>harness</strong> — within an enclosure or between two. Read it back as a <strong>connector-to-connector diagram</strong> or jump straight to the schematic to highlight its wires.</li>
            <li>Export the <strong>wiring list, per-harness sheets and BOM</strong> (Excel / PDF) for the assembler.</li>
          </ol>
          <button style={launchBig} onClick={() => onLaunch()}>
            Start designing →
          </button>
        </section>

        <section aria-label="Why Skematiq" style={whyWrap}>
          <h2 style={h2}>Why Skematiq?</h2>
          <div style={grid}>
            {WHY.map((w) => (
              <div key={w.title} style={card}>
                <h3 style={cardTitle}>{w.title}</h3>
                <p style={cardText}>{w.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section aria-label="Plans" style={plansWrap}>
          <h2 style={h2}>Plans</h2>
          <p style={plansSub}>Start free. Upgrade to Pro when a project gets real.</p>
          <div style={plansGrid}>
            <div style={planCard}>
              <div style={planName}>Free</div>
              <div style={planPrice}>
                $0<span style={planPer}> forever</span>
              </div>
              <ul style={planList}>
                <li>Block diagrams, schematics &amp; harnesses</li>
                <li>PNG export</li>
                <li>{FREE_LIMITS.cloudProjects} cloud-saved project</li>
                <li>{FREE_LIMITS.subsystems} subsystem per project</li>
              </ul>
              <button style={launchGhost} onClick={() => onLaunch()}>
                Start free
              </button>
            </div>
            <div style={planCardPro}>
              <div style={planName}>
                Pro <span style={planBadge}>Most popular</span>
              </div>
              <div style={planPrice}>
                {PRO_PRICE}<span style={planPer}> / month</span>
              </div>
              <ul style={planList}>
                <li>Everything in Free, plus:</li>
                <li>Unlimited projects &amp; subsystems</li>
                <li>Reference photos in your drawings</li>
                <li>PDF drawing-set export</li>
                <li>Excel export — wiring, BOM, netlist</li>
              </ul>
              <button style={launchBig} onClick={() => onLaunch()}>
                Open Skematiq →
              </button>
              <div style={planFoot}>Upgrade inside the app · cancel anytime</div>
            </div>
          </div>
        </section>

        <section aria-label="Frequently asked questions" style={faqWrap}>
          <h2 style={h2}>FAQ</h2>
          <div style={faqList}>
            {FAQ.map((item) => (
              <div key={item.q} style={faqItem}>
                <h3 style={faqQ}>{item.q}</h3>
                <p style={faqA}>{item.a}</p>
              </div>
            ))}
          </div>
        </section>
      </main>

      <footer style={footer}>
        <span>© 2026 Skematiq</span>
        <span style={{ display: 'flex', gap: 14 }}>
          <a href="/privacy.html" style={footerLink}>
            Privacy
          </a>
          <a href="/terms.html" style={footerLink}>
            Terms
          </a>
          <MailLink email="batuhandeveci.bd@gmail.com" />
        </span>
        <span style={{ opacity: 0.7 }}>
          Browser-based electrical CAD for designing schematics, wiring harnesses and connector
          pinouts — with BOM and wiring-diagram exports.
        </span>
      </footer>
    </div>
  );
}

const HERO_CHIPS = ['One connected project', 'Schematics & nets', 'Wiring harnesses', 'Excel & PDF export'];

/* ------------------------------------------------------------------ *
 *  Product illustrations — hand-drawn SVG, not screenshots.
 *
 *  Every figure is a stylised view of the *same* machine as the built-in
 *  example project (a "Conveyor Drive": a 24 V PSU feeds an ESP32 MCU and a
 *  BTS7960 motor driver in the Control Box; the driver's M+/M- run over a
 *  Motor Cable to the gear motor in the Conveyor). Keeping the nets and pinout
 *  consistent across the schematic, block, harness and mechanical views means
 *  they read as one coherent design — and, being SVG, they stay crisp and
 *  never go stale as the real UI changes.
 * ------------------------------------------------------------------ */

const INK = {
  bg: '#0d1117',
  frame: '#2a3341',
  bar: '#131a24',
  grid: 'rgba(148,163,184,0.10)',
  red: '#c93a3f', // power +
  grey: '#4b5568', // ground / return (dark, like the app)
  blue: '#2563d6', // signal
  teal: '#0f8f80', // driver
  purple: '#7c4ddb', // motor / load
  slate: '#2b3648', // connector / boundary blocks
  royal: '#1e40af', // subsystem blocks on the overview
  pin: '#ef5f4c', // connected pin dot
  tag: '#0b0f16', // wire-number chip
  muted: '#8ba3c7',
};

/* Motion — a one-shot "intro" build: the art fades in, then its wires draw
 * themselves on, one after another, like a short product demo. Nothing loops
 * or twitches afterwards. All of it is gated behind prefers-reduced-motion so
 * anyone who has asked the OS to keep things still just sees the finished art. */
const MOTION_CSS = `
@keyframes sk-draw { to { stroke-dashoffset: 0; } }
@keyframes sk-rise { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: no-preference) {
  .sk-draw { stroke-dasharray: 1; stroke-dashoffset: 1; animation: sk-draw 0.75s ease-out both; }
  .sk-rise { animation: sk-rise 0.7s cubic-bezier(0.16, 0.84, 0.44, 1) both; }
}
`;

/** The app window chrome (title bar + dotted canvas) shared by every figure so
 *  the illustrations look like the same tool. `uid` keeps the <defs> ids unique
 *  across the several SVGs on the page. */
function AppFrame({
  uid,
  w,
  h,
  tab,
  style,
  children,
}: {
  uid: string;
  w: number;
  h: number;
  tab: string;
  style?: CSSProperties;
  children: ReactNode;
}) {
  const tabW = 190;
  const tabX = (w - tabW) / 2;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} style={style} role="img" aria-hidden="true">
      <defs>
        <pattern id={`grid-${uid}`} width="22" height="22" patternUnits="userSpaceOnUse">
          <circle cx="1" cy="1" r="1" fill={INK.grid} />
        </pattern>
        <filter id={`sh-${uid}`} x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="10" stdDeviation="16" floodColor="#000" floodOpacity="0.45" />
        </filter>
      </defs>
      <g filter={`url(#sh-${uid})`}>
        <rect x="8" y="8" width={w - 16} height={h - 16} rx="14" fill={INK.bg} stroke={INK.frame} />
        <path d={`M8 22 a14 14 0 0 1 14 -14 h${w - 44} a14 14 0 0 1 14 14 v14 h-${w - 16} z`} fill={INK.bar} />
        <circle cx="26" cy="23" r="4" fill="#e5484d" />
        <circle cx="40" cy="23" r="4" fill="#e2a336" />
        <circle cx="54" cy="23" r="4" fill="#30a46c" />
        <rect x={tabX} y="14" width={tabW} height="17" rx="5" fill="#1d2735" />
        <text x={tabX + 12} y="26.5" fontSize="10" fontFamily="Inter, sans-serif" fill={INK.muted}>
          {tab}
        </text>
        <rect x="9" y="37" width={w - 18} height={h - 45} fill={`url(#grid-${uid})`} />
      </g>
      {children}
    </svg>
  );
}

type PinGroup = { label?: string; pins: string[] };

/** A component block, drawn the way the app does it: solid colour, a header
 *  band with title/subtitle and a divider, then pins grouped down each side.
 *  Each pin is a red "connected" dot on the edge with its name just inside; a
 *  rotated group label (PWR / CTRL / MOTOR …) sits against the edge, centred on
 *  the group's pin span. */
function Block({
  x,
  y,
  w,
  h,
  color,
  title,
  sub,
  left = [],
  right = [],
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  color: string;
  title: string;
  sub?: string;
  left?: PinGroup[];
  right?: PinGroup[];
}) {
  const header = 30;
  const side = (groups: PinGroup[], s: 'left' | 'right') => {
    const n = Math.max(groups.reduce((a, g) => a + g.pins.length, 0), 1);
    const slot = (h - header) / n;
    const px = s === 'left' ? x : x + w;
    const nameX = s === 'left' ? x + 20 : x + w - 20;
    const labelX = s === 'left' ? x + 10 : x + w - 10;
    const anchor = s === 'left' ? 'start' : 'end';
    const out: ReactNode[] = [];
    let idx = 0;
    groups.forEach((g, gi) => {
      const start = idx;
      g.pins.forEach((p) => {
        const py = y + header + (idx + 0.5) * slot;
        out.push(
          <g key={`${s}${gi}-${p}`}>
            <circle cx={px} cy={py} r="3" fill={INK.pin} />
            <text x={nameX} y={py + 2.8} fontSize="7.5" fontFamily="ui-monospace, monospace" fill="rgba(255,255,255,0.92)" textAnchor={anchor}>
              {p}
            </text>
          </g>,
        );
        idx++;
      });
      if (g.label) {
        const cy = y + header + ((start + idx) / 2) * slot;
        out.push(
          <text
            key={`${s}${gi}-lbl`}
            transform={`rotate(-90 ${labelX} ${cy})`}
            x={labelX}
            y={cy}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize="6.5"
            fontWeight="700"
            letterSpacing="0.5"
            fontFamily="Inter, sans-serif"
            fill="rgba(255,255,255,0.55)"
          >
            {g.label}
          </text>,
        );
      }
    });
    return out;
  };
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx="7" fill={color} />
      <path d={`M${x} ${y + 7} a7 7 0 0 1 7 -7 h${w - 14} a7 7 0 0 1 7 7 v${header - 7} h-${w} z`} fill="rgba(0,0,0,0.22)" />
      <line x1={x} y1={y + header} x2={x + w} y2={y + header} stroke="rgba(255,255,255,0.10)" strokeWidth="1" />
      <text x={x + 9} y={y + 15} fontSize="10" fontWeight="700" fontFamily="Inter, sans-serif" fill="#fff">
        {title}
      </text>
      {sub && (
        <text x={x + 9} y={y + 26} fontSize="7.5" fontFamily="ui-monospace, monospace" fill="rgba(255,255,255,0.7)">
          {sub}
        </text>
      )}
      {side(left, 'left')}
      {side(right, 'right')}
    </g>
  );
}

/** A connector symbol (the app's "X1 / X2 / J1" terminal): a shell bracket, a
 *  rotated connector label and red pin dots with names — used on the boundary /
 *  harness blocks where a cable leaves or enters a subsystem. `side` is the
 *  direction the wires run (right = wires exit to the right). */
function Connector({ cx, cy, side, pins, label }: { cx: number; cy: number; side: 'left' | 'right'; pins: string[]; label: string }) {
  const dir = side === 'right' ? 1 : -1;
  const gap = 18;
  const top = cy - ((pins.length - 1) * gap) / 2;
  const anchor = side === 'right' ? 'end' : 'start';
  return (
    <g>
      <path
        d={`M${cx - dir * 7} ${top - 7} h${dir * 7} v${(pins.length - 1) * gap + 14} h${-dir * 7}`}
        fill="none"
        stroke="#8fa3bf"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <text
        transform={`rotate(-90 ${cx - dir * 18} ${cy})`}
        x={cx - dir * 18}
        y={cy}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="8.5"
        fontWeight="700"
        fontFamily="Inter, sans-serif"
        fill="#c7d3e3"
      >
        {label}
      </text>
      {pins.map((p, i) => {
        const py = top + i * gap;
        return (
          <g key={p}>
            <circle cx={cx} cy={py} r="3.2" fill={INK.pin} />
            <text x={cx - dir * 30} y={py + 2.8} textAnchor={anchor} fontSize="8" fontFamily="ui-monospace, monospace" fill="#c7d3e3">
              {p}
            </text>
          </g>
        );
      })}
    </g>
  );
}

/* ---------------------------------------------------------------- *
 *  The walkthrough reel — six steps that take one machine (the example
 *  Control Box) from an empty sheet to a finished panel, in the order you'd
 *  actually work: block it out, wire it, bundle a harness, read the harness
 *  back, focus it on the schematic, then lay it out. Each step is its own
 *  hand-drawn SVG on the shared AppFrame.
 * ---------------------------------------------------------------- */

/** Step 1 — Block diagram: a card per module, linked by the buses that run
 *  between them. The Control link is drawn blue: that's the one that becomes a
 *  harness two steps later. */
function BlockDiagramArt() {
  const MW = 128;
  const MH = 62;
  const mods = {
    psu: { x: 40, y: 128, color: INK.red, title: 'Power Supply', sub: '24 V PSU' },
    mcu: { x: 216, y: 42, color: INK.blue, title: 'Controller', sub: 'MCU' },
    drv: { x: 216, y: 210, color: INK.teal, title: 'Motor Driver', sub: 'H-bridge' },
    mot: { x: 392, y: 128, color: INK.purple, title: 'Gear Motor', sub: 'Conveyor' },
  };
  const cx = (m: { x: number }) => m.x + MW / 2;
  const cy = (m: { y: number }) => m.y + MH / 2;
  // [from, to, label, colour] — the Control bus is the future harness, in blue.
  const links: [keyof typeof mods, keyof typeof mods, string, string, number, number][] = [
    ['psu', 'mcu', 'Power', INK.grey, 190, 116],
    ['psu', 'drv', 'Power', INK.grey, 190, 200],
    ['mcu', 'drv', 'Control', INK.blue, 280, 158],
    ['drv', 'mot', 'Motor', INK.grey, 372, 200],
  ];
  const edgePoint = (a: keyof typeof mods, b: keyof typeof mods) => {
    const m = mods[a];
    const t = mods[b];
    // leave from the side of a that faces b
    const ax = cx(t) > cx(m) + 4 ? m.x + MW : cx(t) < cx(m) - 4 ? m.x : cx(m);
    const ay = ax === cx(m) ? (cy(t) > cy(m) ? m.y + MH : m.y) : cy(m);
    return { x: ax, y: ay };
  };

  return (
    <AppFrame uid="blk" w={560} h={315} tab="Control Box — Block diagram" style={artSvg}>
      {links.map(([a, b, label, color, lx, ly], i) => {
        const p = edgePoint(a, b);
        const q = edgePoint(b, a);
        const mx = (p.x + q.x) / 2;
        const d = `M ${p.x} ${p.y} C ${mx} ${p.y}, ${mx} ${q.y}, ${q.x} ${q.y}`;
        return (
          <g key={label + i}>
            <path d={d} fill="none" stroke={color} strokeWidth={3.5} strokeLinecap="round" pathLength={1} className="sk-draw" style={{ animationDelay: `${0.2 + i * 0.12}s` }} />
            <circle cx={p.x} cy={p.y} r={3} fill={color} />
            <circle cx={q.x} cy={q.y} r={3} fill={color} />
            <g transform={`translate(${lx - label.length * 3.2} ${ly - 8})`}>
              <rect x={0} y={0} width={label.length * 6.4 + 12} height={16} rx={4} fill={INK.bar} stroke={INK.frame} />
              <text x={6} y={11} fontSize={9} fontWeight={700} fontFamily="Inter, sans-serif" fill={color === INK.blue ? '#5b9bff' : INK.muted}>
                {label}
              </text>
            </g>
          </g>
        );
      })}
      {Object.values(mods).map((m) => (
        <g key={m.title}>
          <rect x={m.x} y={m.y} width={MW} height={MH} rx={11} fill={m.color} />
          <path d={`M${m.x} ${m.y + 10} a10 10 0 0 1 10 -10 h${MW - 20} a10 10 0 0 1 10 10 v14 h${-MW} z`} fill="rgba(0,0,0,0.18)" />
          <text x={cx(m)} y={m.y + 34} textAnchor="middle" fontSize={13} fontWeight={800} fontFamily="Inter, sans-serif" fill="#fff">
            {m.title}
          </text>
          <text x={cx(m)} y={m.y + 49} textAnchor="middle" fontSize={9} fontFamily="Inter, sans-serif" fill="rgba(255,255,255,0.82)">
            {m.sub}
          </text>
        </g>
      ))}
    </AppFrame>
  );
}

/** Steps 2, 3 & 5 — the same Control Box schematic, shown three ways: drawn
 *  plain, with the three CTRL wires selected into a harness, and focused (the
 *  "Show in schematic" state) where those wires pop and everything else dims.
 *  One layout, so the story reads as the *same* sheet evolving. */
function ReelSchematic({ mode }: { mode: 'draw' | 'assign' | 'focus' }) {
  const HEADER = 30;
  // Pin coordinates that match the Block component's own placement, so wires
  // land exactly on the dots it draws.
  const pinsOf = (x: number, y: number, w: number, h: number, side: 'left' | 'right', pins: string[]) => {
    const slot = (h - HEADER) / pins.length;
    const px = side === 'left' ? x : x + w;
    const map: Record<string, { x: number; y: number }> = {};
    pins.forEach((p, i) => (map[p] = { x: px, y: y + HEADER + (i + 0.5) * slot }));
    return map;
  };

  const psu = { x: 40, y: 48, w: 126, h: 72 };
  const mcu = { x: 40, y: 196, w: 126, h: 100 };
  const drv = { x: 320, y: 92, w: 152, h: 168 };
  const psuP = pinsOf(psu.x, psu.y, psu.w, psu.h, 'right', ['+24V', '0V']);
  const mcuP = pinsOf(mcu.x, mcu.y, mcu.w, mcu.h, 'right', ['PWM', 'DIR', 'EN']);
  const drvL = pinsOf(drv.x, drv.y, drv.w, drv.h, 'left', ['+24V', 'GND', 'PWM', 'DIR', 'EN']);
  const drvR = pinsOf(drv.x, drv.y, drv.w, drv.h, 'right', ['M+', 'M-']);
  const x1 = { c: { x: 500, y: 196 } };
  const x1P = { '1': { x: 500, y: 187 }, '2': { x: 500, y: 205 } };

  const ortho = (a: { x: number; y: number }, b: { x: number; y: number }, midX: number) =>
    `M ${a.x} ${a.y} H ${midX} V ${b.y} H ${b.x}`;

  // power + motor wires (never part of the harness here)
  const otherWires = [
    { d: ortho(psuP['+24V'], drvL['+24V'], 210), c: INK.red },
    { d: ortho(psuP['0V'], drvL['GND'], 226), c: INK.grey },
    { d: ortho(drvR['M+'], x1P['1'], 486), c: INK.purple },
    { d: ortho(drvR['M-'], x1P['2'], 486), c: INK.purple },
  ];
  // the CTRL harness: MCU → driver, three logic wires
  const ctrlWires = [
    { d: ortho(mcuP['PWM'], drvL['PWM'], 250), c: INK.blue },
    { d: ortho(mcuP['DIR'], drvL['DIR'], 266), c: INK.blue },
    { d: ortho(mcuP['EN'], drvL['EN'], 282), c: INK.blue },
  ];

  const dim = mode === 'focus';
  const hot = mode !== 'draw'; // CTRL wires emphasised

  return (
    <AppFrame uid={`sch-${mode}`} w={560} h={315} tab="Control Box — Schematic" style={artSvg}>
      {/* everything that is not the harness — dimmed when focused */}
      <g opacity={dim ? 0.28 : 1}>
        <g fill="none" strokeWidth={2.4} strokeLinecap="round">
          {otherWires.map((w, i) => (
            <path key={i} d={w.d} stroke={w.c} pathLength={1} className="sk-draw" style={{ animationDelay: `${0.2 + i * 0.1}s` }} />
          ))}
        </g>
        <Block x={psu.x} y={psu.y} w={psu.w} h={psu.h} color={INK.red} title="Power Supply" sub="24 V" right={[{ label: 'DC OUT', pins: ['+24V', '0V'] }]} />
        <Block x={mcu.x} y={mcu.y} w={mcu.w} h={mcu.h} color={INK.blue} title="Controller" sub="MCU" right={[{ label: 'CTRL', pins: ['PWM', 'DIR', 'EN'] }]} />
        <Block
          x={drv.x}
          y={drv.y}
          w={drv.w}
          h={drv.h}
          color={INK.teal}
          title="Motor Driver"
          sub="H-bridge"
          left={[
            { label: 'PWR', pins: ['+24V', 'GND'] },
            { label: 'CTRL', pins: ['PWM', 'DIR', 'EN'] },
          ]}
          right={[{ label: 'MOTOR', pins: ['M+', 'M-'] }]}
        />
        <Block x={x1.c.x} y={150} w={52} h={92} color={INK.slate} title="X1" sub="Motor" />
        <Connector cx={x1.c.x} cy={x1.c.y} side="left" pins={['1', '2']} label="" />
      </g>

      {/* the CTRL harness wires — highlighted in the assign/focus steps */}
      {hot && (
        <g fill="none" strokeLinecap="round" stroke="rgba(37,99,235,0.22)" strokeWidth={9}>
          {ctrlWires.map((w, i) => (
            <path key={i} d={w.d} />
          ))}
        </g>
      )}
      <g fill="none" strokeLinecap="round">
        {ctrlWires.map((w, i) => (
          <path
            key={i}
            d={w.d}
            stroke={hot ? '#2f6fed' : INK.blue}
            strokeWidth={hot ? 3 : 2.4}
            pathLength={1}
            className="sk-draw"
            style={{ animationDelay: `${0.5 + i * 0.12}s` }}
          />
        ))}
      </g>

      {mode === 'assign' && (
        <>
          {/* rubber-band selection round exactly the three CTRL wires */}
          <rect x={156} y={181} width={180} height={116} rx={6} fill="rgba(37,99,235,0.12)" stroke="#5b9bff" strokeWidth={1.6} strokeDasharray="7 5" />
          {/* selection handles at the corners, so it reads as a live marquee */}
          {[
            [156, 181],
            [336, 181],
            [156, 297],
            [336, 297],
          ].map(([hx, hy], i) => (
            <rect key={i} x={hx - 3} y={hy - 3} width={6} height={6} fill="#0d1117" stroke="#5b9bff" strokeWidth={1.4} />
          ))}
          <g transform="translate(148 150)">
            <rect x={0} y={0} width={196} height={24} rx={7} fill="#1d4ed8" stroke="#5b9bff" strokeWidth={1} />
            <text x={13} y={16} fontSize={10.5} fontWeight={700} fontFamily="Inter, sans-serif" fill="#eaf1ff">
              3 wires selected → <tspan fontWeight={800} fill="#fff">Control Harness</tspan>
            </text>
          </g>
        </>
      )}
      {mode === 'focus' && (
        <g transform="translate(20 46)">
          <rect x={0} y={0} width={188} height={22} rx={7} fill="rgba(37,99,235,0.18)" stroke="rgba(37,99,235,0.6)" />
          <circle cx={14} cy={11} r={4} fill="none" stroke="#5b9bff" strokeWidth={1.6} />
          <circle cx={14} cy={11} r={1.6} fill="#5b9bff" />
          <text x={26} y={15} fontSize={9.5} fontFamily="Inter, sans-serif" fill="#c7d3e3">
            Focused <tspan fontWeight={800} fill="#5b9bff">Control Harness</tspan>
          </text>
        </g>
      )}
    </AppFrame>
  );
}

/** Carousel: the Harness Diagram — the connector-to-connector view the app
 *  derives live from the schematic. One harness = its two connectors drawn face
 *  to face, every wire running pin to pin and labelled with the net it carries.
 *  This is the example's Control Harness: the driver's CTRL connector to the
 *  MCU's IO connector, exactly as the app draws it. */
function HarnessDiagramArt() {
  const HEAD = 30;
  const LBL = 18;
  const ROW = 22;
  const BW = 184;
  const WIRE = '#2563d6';
  const DOT = '#1f2a3a';

  // Each side: a component with one connector; every row is a pin and the net
  // it carries. The wire from a left pin runs to the right pin on the same net.
  const leftRows = [
    { pin: 'DIR', net: 'DIR' },
    { pin: 'EN', net: 'EN' },
    { pin: 'PWM', net: 'PWM' },
  ];
  const rightRows = [
    { pin: 'IO2', net: 'PWM' },
    { pin: 'IO4', net: 'DIR' },
    { pin: 'IO5', net: 'EN' },
  ];

  const bh = HEAD + LBL + leftRows.length * ROW + 12;
  const y = Math.round((315 - bh) / 2);
  type Box = { x: number; title: string; conn: string; rows: { pin: string; net: string }[]; side: 'L' | 'R' };
  const L: Box = { x: 40, title: 'DC Motor Driver', conn: 'CTRL', rows: leftRows, side: 'L' };
  const R: Box = { x: 560 - 40 - BW, title: 'MCU', conn: 'IO', rows: rightRows, side: 'R' };
  const pinY = (row: number) => y + HEAD + LBL + row * ROW + ROW / 2;

  // Route each wire by matching nets on the two connectors, then cross as needed.
  const wirePaths = leftRows.map((lr, li) => {
    const ri = rightRows.findIndex((rr) => rr.net === lr.net);
    const ay = pinY(li);
    const by = pinY(ri);
    const ax = L.x + BW;
    const bx = R.x;
    const dx = (bx - ax) * 0.42;
    return `M ${ax} ${ay} C ${ax + dx} ${ay}, ${bx - dx} ${by}, ${bx} ${by}`;
  });

  const renderBox = (b: Box) => {
    const inner = b.side === 'L' ? b.x + BW - 14 : b.x + 14; // pin name, by the dot
    const outer = b.side === 'L' ? b.x + 14 : b.x + BW - 14; // net, on the far edge
    const dotX = b.side === 'L' ? b.x + BW : b.x;
    return (
      <g>
        <rect x={b.x} y={y} width={BW} height={bh} rx={10} fill="#ffffff" stroke="#334155" strokeWidth={1.4} />
        <path d={`M${b.x} ${y + 10} a10 10 0 0 1 10 -10 h${BW - 20} a10 10 0 0 1 10 10 v${HEAD - 10} h${-BW} z`} fill="#eef2f7" />
        <line x1={b.x} y1={y + HEAD} x2={b.x + BW} y2={y + HEAD} stroke="#e2e8f0" />
        <text x={b.x + 14} y={y + 20} fontSize={13} fontWeight={800} fontFamily="Inter, sans-serif" fill="#0f172a">
          {b.title}
        </text>
        {/* connector label band */}
        <rect x={b.x} y={y + HEAD} width={BW} height={LBL} fill="#f1f5f9" />
        <text
          x={b.side === 'L' ? b.x + 14 : b.x + BW - 14}
          y={y + HEAD + 13}
          textAnchor={b.side === 'L' ? 'start' : 'end'}
          fontSize={9.5}
          fontWeight={800}
          letterSpacing={0.5}
          fontFamily="Inter, sans-serif"
          fill="#64748b"
        >
          {b.conn}
        </text>
        {b.rows.map((r, i) => {
          const py = pinY(i);
          return (
            <g key={r.pin}>
              <text
                x={inner}
                y={py + 4}
                textAnchor={b.side === 'L' ? 'end' : 'start'}
                fontSize={11.5}
                fontWeight={700}
                fontFamily="ui-monospace, monospace"
                fill="#0f172a"
              >
                {r.pin}
              </text>
              <text
                x={outer}
                y={py + 4}
                textAnchor={b.side === 'L' ? 'start' : 'end'}
                fontSize={10}
                fontFamily="ui-monospace, monospace"
                fill="#94a3b8"
              >
                {r.net}
              </text>
              <circle cx={dotX} cy={py} r={4} fill={DOT} />
            </g>
          );
        })}
      </g>
    );
  };

  return (
    <AppFrame uid="hd" w={560} h={315} tab="Control Harness — Diagram" style={artSvg}>
      {/* wires drawn in one after another, behind the two connector boxes */}
      <g fill="none" strokeLinecap="round">
        {wirePaths.map((d, i) => (
          <path key={i} d={d} stroke={WIRE} strokeWidth={3} pathLength={1} className="sk-draw" style={{ animationDelay: `${0.3 + i * 0.14}s` }} />
        ))}
      </g>
      {renderBox(L)}
      {renderBox(R)}
    </AppFrame>
  );
}

/** Step 5 — Harness table: the same harness as a wiring table, the way you fill
 *  it in under the harness's tabs. Every wire's endpoints, net, gauge and colour
 *  in one editable grid — one row shown mid-edit to make the "enter details"
 *  point read. */
function HarnessTableArt() {
  const tabs = ['Diagram', 'Connectors', 'Wiring', 'BOM', 'Details'];
  const active = 'Wiring';
  const tabW = (t: string) => t.length * 5.8 + 18;
  let cur = 26;
  const tabItems = tabs.map((t) => {
    const x = cur;
    const w = tabW(t);
    cur += w + 8;
    return { t, x, w };
  });

  const rows = [
    { w: 'W1', from: 'Driver.PWM', to: 'MCU.IO2', net: 'PWM', ga: '24 AWG', c: INK.blue },
    { w: 'W2', from: 'Driver.DIR', to: 'MCU.IO4', net: 'DIR', ga: '24 AWG', c: INK.blue },
    { w: 'W3', from: 'Driver.EN', to: 'MCU.IO5', net: 'EN', ga: '24 AWG', c: INK.blue },
  ];
  const col = { w: 42, from: 96, to: 232, net: 356, ga: 434, sw: 416 };
  const rowY = (i: number) => 158 + i * 34;

  return (
    <AppFrame uid="tbl" w={560} h={315} tab="Control Harness — Wiring" style={artSvg}>
      {/* harness header */}
      <text x={26} y={58} fontSize={12} fontWeight={800} fontFamily="Inter, sans-serif" fill="#e6edf6">
        Control Harness
      </text>
      <text x={152} y={58} fontSize={9} fontFamily="Inter, sans-serif" fill={INK.muted}>
        Control Box → Control Box · 3 wires · 0.3 m
      </text>

      {/* tab strip — Wiring active */}
      {tabItems.map(({ t, x, w }) => (
        <g key={t}>
          <rect x={x} y={70} width={w} height={18} rx={5} fill={t === active ? 'rgba(47,129,247,0.16)' : 'transparent'} stroke={t === active ? '#2f81f7' : INK.frame} />
          <text x={x + 9} y={82} fontSize={9} fontWeight={700} fontFamily="Inter, sans-serif" fill={t === active ? '#5b9bff' : INK.muted}>
            {t}
          </text>
        </g>
      ))}

      {/* table panel */}
      <rect x={22} y={104} width={516} height={182} rx={8} fill="rgba(148,163,184,0.03)" stroke={INK.frame} />
      {/* column headers */}
      {[
        ['Wire', col.w],
        ['From', col.from],
        ['To', col.to],
        ['Net', col.net],
        ['Gauge', col.ga],
      ].map(([t, x]) => (
        <text key={t as string} x={x as number} y={130} fontSize={8.5} fontWeight={800} letterSpacing={0.4} fontFamily="Inter, sans-serif" fill="#9db2d3">
          {t}
        </text>
      ))}
      <line x1={34} y1={138} x2={526} y2={138} stroke={INK.frame} />

      {rows.map((r, i) => {
        const y = rowY(i);
        const editing = i === 0; // one row shown mid-edit
        return (
          <g key={r.w}>
            {editing ? (
              <rect x={34} y={y - 15} width={492} height={26} rx={4} fill="rgba(47,129,247,0.10)" stroke="#2f81f7" strokeWidth={1} />
            ) : (
              i % 2 === 1 && <rect x={34} y={y - 15} width={492} height={26} fill="rgba(148,163,184,0.04)" />
            )}
            <text x={col.w} y={y} fontSize={9.5} fontWeight={700} fontFamily="ui-monospace, monospace" fill="#5b9bff">
              {r.w}
            </text>
            <text x={col.from} y={y} fontSize={9.5} fontFamily="ui-monospace, monospace" fill="#c7d3e3">
              {r.from}
            </text>
            <text x={col.to} y={y} fontSize={9.5} fontFamily="ui-monospace, monospace" fill="#c7d3e3">
              {r.to}
            </text>
            <text x={col.net} y={y} fontSize={9.5} fontFamily="ui-monospace, monospace" fill="#c7d3e3">
              {r.net}
            </text>
            <rect x={col.sw} y={y - 8} width={11} height={6} rx={1} fill={r.c} />
            <text x={col.ga} y={y} fontSize={9.5} fontFamily="ui-monospace, monospace" fill="#c7d3e3">
              {r.ga}
            </text>
            {/* a caret in the edited cell, to read as data entry */}
            {editing && <line x1={col.ga + 44} y1={y - 8} x2={col.ga + 44} y2={y + 3} stroke="#5b9bff" strokeWidth={1.2} />}
          </g>
        );
      })}
    </AppFrame>
  );
}

/** Carousel 3: Mechanical — real-scale panel layout, coloured by part type. */
function MechanicalArt() {
  return (
    <AppFrame uid="mech" w={560} h={315} tab="Control Box — Mechanical" style={artSvg}>
      {/* width dimension line */}
      <g stroke={INK.grey} strokeWidth="1">
        <line x1="70" y1="78" x2="490" y2="78" />
        <line x1="70" y1="72" x2="70" y2="84" />
        <line x1="490" y1="72" x2="490" y2="84" />
      </g>
      <rect x="258" y="70" width="44" height="16" rx="3" fill={INK.bg} />
      <text x="280" y="82" fontSize="9.5" fontFamily="ui-monospace, monospace" fill={INK.muted} textAnchor="middle">
        600 mm
      </text>

      {/* enclosure + a DIN rail carrying the devices */}
      <rect x="70" y="96" width="420" height="176" rx="6" fill="rgba(148,163,184,0.03)" stroke="#3a4658" />
      <line x1="92" y1="146" x2="440" y2="146" stroke="#4a5668" strokeWidth="6" strokeLinecap="round" />
      <line x1="92" y1="146" x2="440" y2="146" stroke="#2a3341" strokeWidth="2" strokeDasharray="2 6" />

      {/* devices to scale, coloured by type */}
      <MechPart x={104} y={118} w={80} h={56} color={INK.red} label="PSU" />
      <MechPart x={198} y={118} w={64} h={56} color={INK.teal} label="Driver" />
      <MechPart x={278} y={124} w={58} h={50} color={INK.blue} label="MCU" />

      {/* X1 — the Control Box's motor-out connector, panel-mounted on the
          chassis wall; the cable to the conveyor (X2, a different enclosure)
          leaves the box here */}
      <text x="484" y="214" fontSize="8.5" fontFamily="Inter, sans-serif" fill={INK.muted} textAnchor="end">
        Motor connector
      </text>
      {/* mounting flange on the inside face of the right wall */}
      <rect x="474" y="220" width="16" height="34" rx="2" fill="#454f5f" />
      {/* connector body through the wall, protruding outside */}
      <rect x="490" y="224" width="24" height="26" rx="4" fill="#5a6675" stroke="#7c8aa0" strokeWidth="1" />
      <circle cx="502" cy="232" r="3.2" fill="#2b3648" stroke="#7c8aa0" strokeWidth="1" />
      <circle cx="502" cy="242" r="3.2" fill="#2b3648" stroke="#7c8aa0" strokeWidth="1" />
      {/* motor cable leaving toward the conveyor */}
      <line x1="514" y1="237" x2="544" y2="237" stroke="#4a5668" strokeWidth="5" strokeLinecap="round" />

      {/* legend */}
      {[
        { c: INK.red, t: 'Power' },
        { c: INK.teal, t: 'Drive' },
        { c: INK.blue, t: 'I/O' },
        { c: '#5a6675', t: 'Connectors' },
      ].map((l, i) => (
        <g key={l.t} transform={`translate(374 ${120 + i * 22})`}>
          <rect x="0" y="0" width="10" height="10" rx="2" fill={l.c} />
          <text x="16" y="9" fontSize="9" fontFamily="Inter, sans-serif" fill={INK.muted}>
            {l.t}
          </text>
        </g>
      ))}
    </AppFrame>
  );
}

function MechPart({ x, y, w, h, color, label }: { x: number; y: number; w: number; h: number; color: string; label: string }) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx="4" fill={color} opacity="0.9" />
      <text x={x + w / 2} y={y + h / 2 + 3.5} fontSize="9.5" fontWeight="700" fontFamily="Inter, sans-serif" fill="#fff" textAnchor="middle">
        {label}
      </text>
    </g>
  );
}

/** Contact link that works even without a configured mail client: it still
 *  opens mailto: if there is one, but also copies the address to the clipboard
 *  and shows "Copied!" so the address is never a dead end. */
function MailLink({ email }: { email: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <a
      href={`mailto:${email}`}
      style={footerLink}
      title={email}
      onClick={() => {
        navigator.clipboard
          ?.writeText(email)
          .then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          })
          .catch(() => {});
      }}
    >
      {copied ? 'Copied!' : 'Contact'}
    </a>
  );
}

/** The walkthrough — six steps, in the order you actually build a machine, all
 *  on the same example Control Box so it reads as one continuous demo. */
const FIGURES: { key: string; caption: string; render: () => ReactNode }[] = [
  {
    key: 'block',
    caption: '1 · Block it out — a card per module, linked by the buses that run between them.',
    render: () => <BlockDiagramArt />,
  },
  {
    key: 'schematic',
    caption: '2 · Draw the schematic — wire pin to pin; the label you type becomes the net.',
    render: () => <ReelSchematic mode="draw" />,
  },
  {
    key: 'assign',
    caption: '3 · Bundle a harness — select the wires that run together and group them.',
    render: () => <ReelSchematic mode="assign" />,
  },
  {
    key: 'harness-diagram',
    caption: '4 · Read the harness back — connector to connector, pin to pin, auto-derived.',
    render: () => <HarnessDiagramArt />,
  },
  {
    key: 'harness-table',
    caption: '5 · Fill in the details — every wire’s endpoints, net, gauge and colour in one table.',
    render: () => <HarnessTableArt />,
  },
  {
    key: 'focus',
    caption: "6 · Hit “Show in schematic” — it jumps back and highlights exactly that harness's wires.",
    render: () => <ReelSchematic mode="focus" />,
  },
  {
    key: 'mechanical',
    caption: '7 · Lay it out — components to real millimetre scale, ready for the panel.',
    render: () => <MechanicalArt />,
  },
];

/** The walkthrough as a self-playing reel: it steps through the six stages of
 *  building a machine, each fading in and drawing itself on like a short looping
 *  demo of the tool. Hovering pauses it; the dots jump straight to a step. */
function Showcase() {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const n = FIGURES.length;

  useEffect(() => {
    if (paused) return;
    const t = setTimeout(() => setI((v) => (v + 1) % n), 4200);
    return () => clearTimeout(t);
  }, [i, paused, n]);

  const fig = FIGURES[i];
  return (
    <section aria-label="Illustrated views" style={showcaseWrap}>
      <div style={slideFrame} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
        {/* keyed on the index so each step remounts and replays the draw-in */}
        <div key={i} className="sk-rise" style={slideInner}>
          {fig.render()}
        </div>
      </div>
      <figcaption style={shotCaption}>{fig.caption}</figcaption>
      <div style={dotRow}>
        {FIGURES.map((f, idx) => (
          <button
            key={f.key}
            type="button"
            aria-label={`Show ${f.key}`}
            aria-current={idx === i}
            onClick={() => setI(idx)}
            style={{ ...dot, ...(idx === i ? dotActive : {}) }}
          />
        ))}
      </div>
    </section>
  );
}

const FEATURES = [
  {
    title: 'Block diagrams',
    text: 'Sketch the high-level architecture — modules and the harnesses that run between them — before drawing any detail.',
  },
  {
    title: 'Schematics & nets',
    text: 'Place components, wire pin to pin with clean orthogonal routing, and name nets. A rule checker flags conflicts and floating nets.',
  },
  {
    title: 'Wiring harnesses',
    text: 'Group schematic wires into production harnesses — each with an auto connector pinout table, wire numbers, terminations and part numbers, plus a connector-to-connector diagram you can trace net by net.',
  },
  {
    title: 'Mechanical layout',
    text: 'Lay out enclosures and components to real millimetre scale, auto-coloured by part type with a legend and a measure tool.',
  },
  {
    title: 'Requirements & documents',
    text: 'Capture requirements per subsystem and write free-form installation / maintenance documents alongside the design.',
  },
  {
    title: 'Production exports',
    text: 'Export wiring lists, a per-harness worksheet for each cable, a bill of materials, netlists and a full PDF drawing set.',
  },
];

const WHY = [
  {
    title: 'Everything in one project',
    text: "Block diagrams, schematics, harnesses and layout live together and stay in sync — change a wire and its harness table, netlist and BOM follow. And it's focused on machine wiring, not a thousand-feature EDA suite, so there's nothing to reconcile by hand.",
  },
  {
    title: 'Runs in your browser',
    text: 'No install, no license dongle, nothing to maintain. Open it on any computer and pick up where you left off — try the example without even signing up.',
  },
  {
    title: 'Priced for real work',
    text: `Free to start; Pro is ${PRO_PRICE}/mo — a fraction of traditional electrical CAD, with no seat contracts or renewals to chase.`,
  },
];

// Keep these answers in sync with the FAQPage JSON-LD in index.html — Google
// requires the structured data to match the visible text on the page.
const FAQ = [
  {
    q: 'Do I need to install anything?',
    a: "No — Skematiq runs entirely in your browser. There's nothing to install and no license dongle, and you can open the example project without even signing up.",
  },
  {
    q: 'Is it really free?',
    a: `There's a free plan you can use forever — block diagrams, schematics, harnesses, PNG export and one cloud-saved project. Pro (${PRO_PRICE}/mo) adds unlimited projects and subsystems, reference photos, and Excel/PDF exports.`,
  },
  {
    q: 'Where is my data stored?',
    a: 'Your work autosaves locally in your browser. It only leaves your device if you choose to save a project to the cloud under your account.',
  },
  {
    q: 'What can I export?',
    a: 'Wiring lists, a per-harness worksheet for each cable, a bill of materials (BOM), netlists and a full PDF drawing set. PNG export is available on the free plan.',
  },
  {
    q: 'How is it different from Altium or EPLAN?',
    a: "Skematiq focuses on the electrical side of machines and robots and keeps things simple and tidy. It isn't a full PCB/EDA suite — it's for wiring machines correctly and handing the assembler clean documentation, in the browser, at a fraction of the cost.",
  },
];

const page: CSSProperties = {
  minHeight: '100vh',
  width: '100%',
  overflowY: 'auto',
  background: 'radial-gradient(1200px 600px at 50% -10%, #172234 0%, var(--bg) 60%)',
  color: 'var(--text)',
  fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
  display: 'flex',
  flexDirection: 'column',
};
const navStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '16px 24px',
  maxWidth: 1040,
  width: '100%',
  margin: '0 auto',
  boxSizing: 'border-box',
};
const heroWrap: CSSProperties = {
  flex: 1,
  maxWidth: 1240,
  width: '100%',
  margin: '0 auto',
  padding: '0 24px 40px',
  boxSizing: 'border-box',
};
// Hero is now a centred headline block; the walkthrough reel below is the wide
// visual, so the intro reads "big statement → watch it work".
const hero: CSSProperties = {
  padding: '52px 0 30px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
};
const heroText: CSSProperties = { maxWidth: 760, margin: '0 auto' };
const eyebrow: CSSProperties = {
  display: 'inline-block',
  fontSize: 11.5,
  fontWeight: 800,
  letterSpacing: 0.6,
  textTransform: 'uppercase',
  color: 'var(--accent)',
  background: 'rgba(47,129,247,0.12)',
  border: '1px solid var(--accent-border)',
  borderRadius: 999,
  padding: '5px 12px',
  marginBottom: 16,
};
const h1: CSSProperties = { fontSize: 46, lineHeight: 1.1, fontWeight: 800, margin: '0 0 18px', letterSpacing: '-0.5px' };
const sub: CSSProperties = { fontSize: 16.5, lineHeight: 1.6, color: 'var(--text-muted)', margin: '0 0 26px' };
const ctaRow: CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, flexWrap: 'wrap' };
const ctaNote: CSSProperties = { display: 'block', fontSize: 12.5, color: 'var(--text-dim)', marginTop: 12 };
const chips: CSSProperties = {
  listStyle: 'none',
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: 8,
  margin: '22px 0 0',
  padding: 0,
};
const chip: CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  color: 'var(--text-muted)',
  background: 'var(--panel)',
  border: '1px solid var(--panel-border)',
  borderRadius: 8,
  padding: '5px 10px',
};
// Carousel figures fill their 16:9 frame (viewBox is 16:9 too, so no letterbox).
const artSvg: CSSProperties = { width: '100%', height: '100%', display: 'block' };
const launchBig: CSSProperties = {
  border: 'none',
  borderRadius: 'var(--radius)',
  background: 'var(--accent)',
  color: '#fff',
  fontSize: 15,
  fontWeight: 700,
  padding: '12px 22px',
  cursor: 'pointer',
  boxShadow: '0 6px 20px rgba(47,129,247,0.35)',
};
// Quiet nav link — persistent access to the demo without competing with the
// primary hero CTA.
const navGhost: CSSProperties = {
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  background: 'transparent',
  color: 'var(--text-muted)',
  fontSize: 13,
  fontWeight: 700,
  padding: '7px 14px',
  cursor: 'pointer',
};
// Secondary hero CTA shown next to "Continue where you left off".
const launchGhost: CSSProperties = {
  border: '1px solid var(--accent-border)',
  borderRadius: 'var(--radius)',
  background: 'transparent',
  color: 'var(--accent)',
  fontSize: 15,
  fontWeight: 700,
  padding: '11px 22px',
  cursor: 'pointer',
};
const grid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: 16,
  margin: '8px 0 44px',
};

/* ---- illustrated views — a single, auto-advancing "reel" ---- */
const showcaseWrap: CSSProperties = { margin: '4px 0 44px' };
const slideFrame: CSSProperties = {
  position: 'relative',
  width: '100%',
  maxWidth: 1080,
  margin: '0 auto',
  aspectRatio: '16 / 9',
  borderRadius: 'var(--radius)',
  border: '1px solid var(--panel-border)',
  background: '#0d1117',
  overflow: 'hidden',
  boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
};
const slideInner: CSSProperties = { width: '100%', height: '100%' };
const shotCaption: CSSProperties = {
  maxWidth: 1080,
  margin: '12px auto 0',
  fontSize: 12.5,
  color: 'var(--text-muted)',
  lineHeight: 1.4,
  textAlign: 'center',
};
const dotRow: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  gap: 8,
  marginTop: 14,
};
const dot: CSSProperties = {
  width: 8,
  height: 8,
  padding: 0,
  borderRadius: '50%',
  border: 'none',
  background: 'var(--panel-border)',
  cursor: 'pointer',
  transition: 'background 0.2s, width 0.2s',
};
const dotActive: CSSProperties = { width: 22, borderRadius: 999, background: 'var(--accent)' };

const card: CSSProperties = {
  background: 'var(--panel)',
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius)',
  padding: '18px 18px 16px',
};
const cardTitle: CSSProperties = { fontSize: 15, fontWeight: 800, margin: '0 0 6px', color: 'var(--text)' };
const cardText: CSSProperties = { fontSize: 13, lineHeight: 1.55, color: 'var(--text-muted)', margin: 0 };
const howWrap: CSSProperties = { maxWidth: 760, marginBottom: 20 };
const whyWrap: CSSProperties = { margin: '8px 0 40px' };
const faqWrap: CSSProperties = { maxWidth: 760, margin: '8px 0 44px' };
const faqList: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 18 };
const faqItem: CSSProperties = {
  borderLeft: '2px solid var(--panel-border)',
  paddingLeft: 14,
};
const faqQ: CSSProperties = { fontSize: 15, fontWeight: 800, margin: '0 0 5px', color: 'var(--text)' };
const faqA: CSSProperties = { fontSize: 13.5, lineHeight: 1.6, color: 'var(--text-muted)', margin: 0 };
const h2: CSSProperties = { fontSize: 24, fontWeight: 800, margin: '0 0 14px', letterSpacing: '-0.3px' };
const plansWrap: CSSProperties = { maxWidth: 760, margin: '8px 0 40px' };
const plansSub: CSSProperties = { fontSize: 14, color: 'var(--text-muted)', margin: '-6px 0 20px' };
const plansGrid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
  gap: 16,
};
const planCard: CSSProperties = {
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius)',
  background: 'var(--panel)',
  padding: 20,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};
const planCardPro: CSSProperties = {
  ...planCard,
  border: '1px solid var(--accent)',
  boxShadow: '0 0 0 1px var(--accent) inset',
};
const planName: CSSProperties = {
  fontSize: 15,
  fontWeight: 800,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};
const planBadge: CSSProperties = {
  fontSize: 9.5,
  fontWeight: 800,
  letterSpacing: 0.5,
  textTransform: 'uppercase',
  color: '#fff',
  background: 'var(--accent)',
  borderRadius: 10,
  padding: '2px 7px',
};
const planPrice: CSSProperties = { fontSize: 30, fontWeight: 800, letterSpacing: '-0.5px' };
const planPer: CSSProperties = { fontSize: 13, fontWeight: 600, color: 'var(--text-muted)' };
const planList: CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  fontSize: 13,
  lineHeight: 1.45,
  color: 'var(--text-muted)',
  flex: 1,
};
const planFoot: CSSProperties = { fontSize: 11.5, color: 'var(--text-dim)', textAlign: 'center' };
const steps: CSSProperties = { margin: '0 0 24px', paddingLeft: 20, fontSize: 14.5, lineHeight: 1.7, color: 'var(--text-muted)' };
const footer: CSSProperties = {
  borderTop: '1px solid var(--panel-border)',
  padding: '18px 24px',
  maxWidth: 1040,
  width: '100%',
  margin: '0 auto',
  boxSizing: 'border-box',
  display: 'flex',
  justifyContent: 'space-between',
  gap: 16,
  flexWrap: 'wrap',
  fontSize: 12,
  color: 'var(--text-dim)',
};
const footerLink: CSSProperties = { color: 'var(--text-muted)', textDecoration: 'none' };
